import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Subscription } from 'rxjs';
import { SocketStatus } from '../../../data-models/socket-base.data-model';
import {
  ChatSocketMessageType,
  IChatMessage,
  ISocketConnectedResponse,
  SocketAgentActivity,
  SocketAiResponseStart,
  SocketConversationTitleUpdated,
  SocketErrorResponse,
  SocketGenerationCancelled,
  SocketMessageResponseChunk,
  SocketMessageResponseComplete,
} from '../../../schemas/agent-conversation.schema';
import { HudsonWsService } from '../../../services/HudsonWsService';
import { queryClient } from '../../../services/MaggieQueryClient';
import { customerTenantIdState, readCustomerTenantId } from '../../../services/state/AuthState';
import { useToken } from '../../../services/hooks/useToken';
import { AgentWsIdentityAtom, ChatConversationsQueryKey, useResetChatState } from './chatActionHooks';

interface IUseWsChatParams {
  onFullMessageResponse?: (update: SocketMessageResponseComplete) => void;
  onConnectionEstablished?: (update: ISocketConnectedResponse) => void;
  onAiResponseStart?: (update: SocketAiResponseStart) => void;
  onMessageResponseChunk?: (update: SocketMessageResponseChunk) => void;
  onTitleUpdated?: (update: SocketConversationTitleUpdated) => void;
  onError?: (update: SocketErrorResponse) => void;
  onGenerationCancelled?: (update: SocketGenerationCancelled) => void;
  onAgentActivity?: (update: SocketAgentActivity) => void;
}
export function useWsChat({
  onError,
  onConnectionEstablished,
  onAiResponseStart,
  onFullMessageResponse,
  onGenerationCancelled,
  onMessageResponseChunk,
  onTitleUpdated,
  onAgentActivity,
}: IUseWsChatParams) {
  const getToken = useToken();
  const resetChatState = useResetChatState();
  const customerTenantId = useAtomValue(customerTenantIdState);
  const setAgentWsIdentity = useSetAtom(AgentWsIdentityAtom);
  const lastTenantIdRef = useRef(customerTenantId);

  // A socket that connected before the customer was known bound itself to the
  // token's tenant, and the conversation list it delivered belongs there too
  useEffect(() => {
    const previous = lastTenantIdRef.current;
    lastTenantIdRef.current = customerTenantId;
    if (!customerTenantId || customerTenantId === previous) {
      return;
    }
    const service = HudsonWsService.get();
    if (!service || service.socketStatusSync === SocketStatus.closed) {
      return;
    }
    // The old identity names the token's tenant; drop it so the mismatch banner
    // waits for the new handshake instead of flashing during the reconnect
    setAgentWsIdentity(null);
    void service.reconnect();
    queryClient.invalidateQueries({ queryKey: [ChatConversationsQueryKey] });
  }, [customerTenantId, setAgentWsIdentity]);

  const sendWsMessage = useCallback((message: IChatMessage) => {
    if (!HudsonWsService.get()) {
      // should not happen
      Sentry.logger.error('Service not initialized');
      return;
    }
    HudsonWsService.get().send(message);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const subs: Subscription[] = [];
    Sentry.logger.info('subscribing to chat');

    HudsonWsService.initService(getToken, readCustomerTenantId).then(() => {
      if (cancelled) {
        return;
      } // Don't subscribe if unmounted

      const connectionSub = HudsonWsService.get().listen<ISocketConnectedResponse>(
        ChatSocketMessageType.connectionEstablished,
        (update) => {
          Sentry.logger.info('ws chat connection established');
          onConnectionEstablished?.(update);
        }
      );
      const messageSub = HudsonWsService.get().listen<SocketMessageResponseComplete>(
        ChatSocketMessageType.aiResponseComplete,
        (update) => {
          onFullMessageResponse?.(update);
        }
      );

      const messageChunkSub = HudsonWsService.get().listen<SocketMessageResponseChunk>(
        ChatSocketMessageType.aiResponseChunk,
        (update) => {
          onMessageResponseChunk?.(update);
        }
      );

      const aiResponseStartSub = HudsonWsService.get().listen<SocketAiResponseStart>(
        ChatSocketMessageType.aiResponseStart,
        (update) => {
          onAiResponseStart?.(update);
        }
      );

      const titleUpdatedSub = HudsonWsService.get().listen<SocketConversationTitleUpdated>(
        ChatSocketMessageType.titleUpdated,
        (update) => {
          onTitleUpdated?.(update);
        }
      );

      const onGenerationCancelledSub = HudsonWsService.get().listen<SocketGenerationCancelled>(
        ChatSocketMessageType.generationCanceled,
        (update) => {
          onGenerationCancelled?.(update);
        }
      );

      const onErrorSub = HudsonWsService.get().listen<SocketErrorResponse>(
        ChatSocketMessageType.error,
        (update) => {
          onError?.(update);
        }
      );

      const agentActivitySub = HudsonWsService.get().listen<SocketAgentActivity>(
        ChatSocketMessageType.agentActivity,
        (update) => {
          onAgentActivity?.(update);
        }
      );

      subs.push(
        connectionSub,
        messageSub,
        messageChunkSub,
        aiResponseStartSub,
        titleUpdatedSub,
        onGenerationCancelledSub,
        onErrorSub,
        agentActivitySub
      );
    });

    return () => {
      Sentry.logger.info('unsubscribing from chat');
      cancelled = true;
      subs.forEach((sub) => sub.unsubscribe());
    };
  }, [
    getToken,
    onConnectionEstablished,
    onAiResponseStart,
    onFullMessageResponse,
    onMessageResponseChunk,
    onTitleUpdated,
    onGenerationCancelled,
    onError,
    onAgentActivity,
    resetChatState,
  ]);

  useEffect(() => {
    return () => {
      HudsonWsService.get()?.disconnect();
      resetChatState();
    };
  }, [resetChatState]);

  return {
    sendMessage: sendWsMessage,
  };
}

export function useChatWsStatus(): SocketStatus | undefined {
  const [status, setStatus] = useState<SocketStatus>();

  useEffect(() => {
    const wsService = HudsonWsService.get();
    if (!wsService) {
      return;
    }

    const sub = wsService.socketStatus.subscribe(setStatus);
    return () => sub.unsubscribe();
  }, []);

  return status;
}
